import React from 'react'
import { Tooltip } from '@mui/material'
import {
  AddCircleOutline as CreateIcon, DeleteOutline as DeleteIcon, EditOutlined as UpdateIcon,
  PowerSettingsNew as PowerIcon, Inventory2Outlined as PackageIcon, ManageSearch as WorkflowIcon,
} from '@mui/icons-material'
import { api } from '../../function/axios'
import s from './AuditLog.module.css'

/*
 * The catalogue journal — what admins changed in packages and search
 * workflows (backend: admin_audit, GET /admin-dashboard/audit). Newest first,
 * grouped by day; each entry says who, when, and what changed field by field
 * ("Price €49 → €59", "Workflows + Phone Lookup"). A created or deleted
 * record keeps its full snapshot, a click away.
 *
 * Scoped to one package / workflow (entityType + entityId) it is that item's
 * History; unscoped it is the whole catalogue, with a Packages / Workflows
 * filter.
 */

type Entry = {
  id: number
  adminLogin?: string | null
  entityType: 'package' | 'lookup'
  entityId?: number | null
  entityTitle?: string | null
  action: 'create' | 'update' | 'delete' | 'activate' | 'deactivate' | string
  changes?: Record<string, any> | null
  snapshot?: Record<string, any> | null
  createdTime: string
}

const PAGE = 30

const ACTION: Record<string, { label: string; color: string; icon: React.ReactNode }> = {
  create: { label: 'Created', color: 'var(--ok)', icon: <CreateIcon sx={{ fontSize: 16 }} /> },
  update: { label: 'Edited', color: 'var(--primary)', icon: <UpdateIcon sx={{ fontSize: 16 }} /> },
  delete: { label: 'Deleted', color: 'var(--danger)', icon: <DeleteIcon sx={{ fontSize: 16 }} /> },
  activate: { label: 'Activated', color: 'var(--ok)', icon: <PowerIcon sx={{ fontSize: 16 }} /> },
  deactivate: { label: 'Deactivated', color: 'var(--text3)', icon: <PowerIcon sx={{ fontSize: 16 }} /> },
}

const FIELD: Record<string, string> = {
  title: 'Title', subTitle: 'Subtitle', price: 'Price', bonus: 'Bonus', button: 'Button', activeDay: 'Period (days)',
  isFree: 'Welcome (free)', isActive: 'Active', isIrbisPro: 'Irbis Pro', isBestProposition: 'Best offer', isAiAssistant: 'AI assistant',
  uid: 'Owner uid', type: 'Type', packageDomain: 'Domain', productId: 'PayPro product', creditsPerUnit: 'Credits per €',
  searchRuleType: 'Results from DB', getRequestsCount: 'Evaluation', maxKeysNum: 'Max API keys', annualPrice: 'Annual price',
  annualProductId: 'Annual PayPro product', isAnnual: 'Annual plan', apiLimits: 'API limits', feature1: 'Feature 1',
  feature2: 'Feature 2', feature3: 'Feature 3', feature4: 'Feature 4', sources: 'Sources', input: 'Input hint', color: 'Color',
  maxNum: 'Max in list', fakeCheckPrice: 'Fake check price', aiSummarizingPrice: 'AI summary price', lookups: 'Workflows',
}
const MONEY = new Set(['price', 'bonus', 'annualPrice', 'fakeCheckPrice', 'aiSummarizingPrice'])

function show(field: string, v: any): string {
  if (v === null || v === undefined || v === '') return '—'
  if (typeof v === 'boolean') return v ? 'on' : 'off'
  if (MONEY.has(field) && !Number.isNaN(Number(v))) return `€${v}`
  if (Array.isArray(v)) return v.length ? v.join(', ') : '—'
  if (typeof v === 'object') {
    if ('context' in v) return String(v.context || '—')                // subtitle
    if ('title' in v) return String(v.title || '—')                    // feature
    if ('requestLimit' in v) return `${v.requestLimit} / ${v.requestInterval}`
    return JSON.stringify(v)
  }
  return String(v)
}

const pad = (n: number) => String(n).padStart(2, '0')
const timeOf = (d: Date) => `${pad(d.getHours())}:${pad(d.getMinutes())}`
const dayOf = (d: Date) => d.toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric' })

function Changes({ changes }: { changes: Record<string, any> }) {
  return (
    <div className={s.changes}>
      {Object.entries(changes).map(([field, c]) => {
        if (field === 'lookups' && c && (c.added || c.removed)) {
          return (
            <div key={field} className={s.change}>
              <span className={s.field}>{FIELD[field] || field}</span>
              <span className={s.list}>
                {(c.added || []).map((x: string) => <span key={`+${x}`} className={s.add}>+ {x}</span>)}
                {(c.removed || []).map((x: string) => <span key={`-${x}`} className={s.del}>− {x}</span>)}
              </span>
            </div>
          )
        }
        return (
          <div key={field} className={s.change}>
            <span className={s.field}>{FIELD[field] || field}</span>
            <span className={s.from} title={show(field, c?.from)}>{show(field, c?.from)}</span>
            <span className={s.arrow}>→</span>
            <span className={s.to} title={show(field, c?.to)}>{show(field, c?.to)}</span>
          </div>
        )
      })}
    </div>
  )
}

export function AuditLog({ entityType, entityId, compact }: {
  entityType?: 'package' | 'lookup'
  entityId?: number
  /** inside a dialog: no entity names (it is one item), no kind filter */
  compact?: boolean
}) {
  const [kind, setKind] = React.useState<'all' | 'package' | 'lookup'>(entityType || 'all')
  const [rows, setRows] = React.useState<Entry[]>([])
  const [count, setCount] = React.useState(0)
  const [loading, setLoading] = React.useState(true)
  const [error, setError] = React.useState('')
  const [open, setOpen] = React.useState<number | null>(null)

  const load = React.useCallback(async (offset: number) => {
    setLoading(true)
    setError('')
    try {
      const params: Record<string, any> = { offset, limit: PAGE }
      if (entityType) params.entityType = entityType
      else if (kind !== 'all') params.entityType = kind
      if (entityId != null) params.entityId = entityId
      const { data } = await api({ method: 'GET', url: '/admin-dashboard/audit', params })
      const list: Entry[] = Array.isArray(data?.list) ? data.list : []
      setRows((prev) => (offset ? [...prev, ...list] : list))
      setCount(Number(data?.count) || 0)
    } catch (e) {
      setError('Could not load the history')
    } finally {
      setLoading(false)
    }
  }, [entityType, entityId, kind])

  React.useEffect(() => { void load(0) }, [load])

  let lastDay = ''
  return (
    <div className={`${s.wrap} ${compact ? s.compact : ''}`}>
      {!compact && !entityType && (
        <div className={s.head}>
          <div className={s.chips}>
            {([['all', 'All changes'], ['package', 'Packages'], ['lookup', 'Workflows']] as const).map(([k, label]) => (
              <button key={k} type="button" className={`${s.chip} ${kind === k ? s.chipOn : ''}`} onClick={() => setKind(k)}>{label}</button>
            ))}
          </div>
          <span className={s.count}>{count} change{count === 1 ? '' : 's'}</span>
        </div>
      )}

      {error && <div className={s.empty}>{error}</div>}
      {!error && !loading && !rows.length && (
        <div className={s.empty}>{compact ? 'No changes recorded yet' : 'No catalogue changes recorded yet'}</div>
      )}

      <div className={s.timeline}>
        {rows.map((r) => {
          const d = new Date(r.createdTime)
          const day = Number.isNaN(d.getTime()) ? '' : dayOf(d)
          const showDay = day && day !== lastDay
          if (day) lastDay = day
          const a = ACTION[r.action] || { label: r.action, color: 'var(--text3)', icon: <UpdateIcon sx={{ fontSize: 16 }} /> }
          const changes = r.changes && Object.keys(r.changes).length ? r.changes : null
          const isOpen = open === r.id
          return (
            <React.Fragment key={r.id}>
              {showDay && <div className={s.day}>{day}</div>}
              <div className={s.item}>
                <span className={s.icon} style={{ color: a.color, background: `color-mix(in srgb, ${a.color} 13%, transparent)` }}>{a.icon}</span>
                <div className={s.body}>
                  <div className={s.title}>
                    <b>{a.label}</b>
                    {!compact && (
                      <span className={s.entity}>
                        {r.entityType === 'package' ? <PackageIcon sx={{ fontSize: 14 }} /> : <WorkflowIcon sx={{ fontSize: 14 }} />}
                        {r.entityTitle || `#${r.entityId}`}
                      </span>
                    )}
                  </div>
                  {changes && <Changes changes={changes} />}
                  {r.snapshot && (
                    <button type="button" className={s.snapBtn} onClick={() => setOpen(isOpen ? null : r.id)}>
                      {isOpen ? 'Hide record' : r.action === 'delete' ? 'Show the deleted record' : 'Show the record'}
                    </button>
                  )}
                  {isOpen && r.snapshot && <pre className={s.snap}>{JSON.stringify(r.snapshot, null, 2)}</pre>}
                  <div className={s.meta}>
                    <span className={s.by}>{r.adminLogin || 'unknown admin'}</span>
                  </div>
                </div>
                <Tooltip title={Number.isNaN(d.getTime()) ? '' : d.toLocaleString()}>
                  <span className={s.time}>{Number.isNaN(d.getTime()) ? '—' : timeOf(d)}</span>
                </Tooltip>
              </div>
            </React.Fragment>
          )
        })}
      </div>

      {loading && <div className={s.empty}>Loading…</div>}
      {!loading && rows.length < count && (
        <button type="button" className={s.more} onClick={() => { void load(rows.length) }}>Show older</button>
      )}
    </div>
  )
}
